import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { BulkDownloadResponse } from "../../interfaces/exercise-file-upload-download-request";
import {
  BulkDownloadCompleteBody,
  BulkDownloadProgressBody,
} from "../../interfaces/websocket-message";

export type BulkDownloadPhase = "idle" | "generating" | "downloading";

export interface BulkDownloadState {
  id: string | null;
  phase: BulkDownloadPhase;
  uploadedBytes: number;
  totalBytes: number;
}

const initialState: BulkDownloadState = {
  id: null,
  phase: "idle",
  uploadedBytes: 0,
  totalBytes: 0,
};

@Injectable({
  providedIn: "root",
})
export class BulkDownloadService {
  private stateSubject = new BehaviorSubject<BulkDownloadState>(initialState);
  private downloadReadySubject = new Subject<BulkDownloadResponse>();
  private rafId: number | null = null;
  private pendingState: BulkDownloadState | null = null;
  private activeDownloadId: string | null = null;

  state$: Observable<BulkDownloadState> = this.stateSubject.asObservable();
  downloadReady$: Observable<BulkDownloadResponse> =
    this.downloadReadySubject.asObservable();

  startGenerating(id: string): void {
    this.stateSubject.next({
      ...initialState,
      id,
      phase: "generating",
    });
  }

  startDownloading(id: string): boolean {
    if (this.activeDownloadId !== null) {
      return false;
    }
    this.activeDownloadId = id;
    this.stateSubject.next({
      ...this.stateSubject.value,
      phase: "downloading",
    });
    return true;
  }

  updateProgress(progress: BulkDownloadProgressBody): void {
    const currentState = this.stateSubject.value;
    if (currentState.phase === "downloading") {
      return;
    }
    if (currentState.id !== null && currentState.id !== progress.id) {
      return;
    }

    this.pendingState = {
      ...currentState,
      id: progress.id,
      phase: "generating",
      uploadedBytes: progress.uploaded_bytes,
      totalBytes: progress.total_bytes,
    };

    this.scheduleStateUpdate();
  }

  complete(body: BulkDownloadCompleteBody): void {
    const { efubd } = body;
    const currentState = this.stateSubject.value;

    if (this.activeDownloadId !== null) {
      return;
    }

    if (currentState.id !== efubd.id && currentState.phase !== "idle") {
      return;
    }

    if (this.startDownloading(efubd.id)) {
      this.downloadReadySubject.next(efubd);
    }
  }


  private scheduleStateUpdate(): void {
    if (this.rafId === null) {
      this.rafId = requestAnimationFrame(() => {
        if (this.pendingState) {
          this.stateSubject.next(this.pendingState);
          this.pendingState = null;
        }
        this.rafId = null;
      });
    }
  }

  reset(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    this.pendingState = null;
    this.activeDownloadId = null;
    this.stateSubject.next(initialState);
  }

  finishDownload(): void {
    this.activeDownloadId = null;
    this.reset();
  }
}

